// bottom_editor_dock: where the phone composer pill stands at a thread panel's
// foot, taken from one measured quantity per surface instead of predictions
// (docs/bottom-editor-keyboard-no-tracker.md).
//
// Two layout modes exist, and every surface is one or the other:
//
// - resize: the layout viewport shrinks for the keyboard (Android browsers
//   and shells, and an iOS shell that resizes its WebView). The panel's foot
//   already rides the keyboard's edge; the dock only drops the resting
//   clearance (navbar, inset) while the keyboard occupies that edge. Whether
//   it does is read from the measured shrink of the viewport, never from the
//   shell's keyboard state, which a replayed resize does not publish.
// - overlay: the layout viewport stays put and the keyboard covers its foot
//   (iOS Safari and PWAs). The dock lifts the pill by how far the keyboard's
//   edge has risen past the pill's resting rail, from the visual viewport.
//
// The pill is placed by one CSS variable on its host, written here directly —
// never through a React render per sample. React consumers subscribe only to
// the coarse snapshot (is a keyboard present, is it being dismissed).

import { getKeyboardEdgeAdapter, peekKeyboardEdge } from 'lib/keyboardEdgeAdapter'

export const BOTTOM_EDITOR_DOCK_SHIFT_VAR = '--bottom-editor-dock-shift'
export const BOTTOM_EDITOR_NAVIGATION_OPACITY_VAR = '--bottom-editor-navigation-opacity'
// A keyboard occupies more than any browser chrome change (Safari's toolbar,
// Chrome's URL bar) shrinks the viewport by.
export const KEYBOARD_PRESENCE_THRESHOLD_PX = 120
// The pill's return to its rail at the start of a dismissal runs over about
// the keyboard's own slide.
export const DOCK_RETURN_MS = 250
export const DOCK_RETURN_EASING = 'cubic-bezier(0.2, 0, 0, 1)'
// Smaller moves are not worth animating back from.
export const DOCK_RETURN_MIN_PX = 24
// A keyboard expected at a focus that has not taken the viewport by then
// never came (a hardware keyboard, a blocked focus).
export const DOCK_EXPECT_KEYBOARD_MS = 1000
// The navbar rises back from the edge over this window (control.module.scss
// mobileControlReturn); the attribute that runs it is cleared after.
export const DOCK_NAVIGATION_RETURN_MS = 260

// On for everyone, with the bottom editor. The check stays the one place that
// asks, so the prediction it replaces (PanelController) can be taken out
// behind it.
export function bottomEditorDockEnabled(): boolean {
    return true
}

export type DockMode = 'overlay' | 'resize'

type NativeCaps = { keyboardResize?: boolean; keyboardState?: boolean } | null | undefined

export function resolveDockMode({
    isAndroid,
    nativeCaps,
}: {
    isAndroid: boolean
    nativeCaps: NativeCaps
}): DockMode {
    if (isAndroid) return 'resize'
    if (nativeCaps?.keyboardResize === true) return 'resize'
    return 'overlay'
}

// How far the keyboard covers the panel from below, in the page's layout
// coordinates: the panel's bottom less the visible viewport's bottom
// (visualViewport.offsetTop + height). With the page panned by WebKit the
// visible bottom sits lower in the layout, and the covered part shrinks with it.
export function resolveOverlayObstruction({
    panelBottom,
    visibleBottom,
}: {
    panelBottom: number
    visibleBottom: number
}): number {
    const covered = Number(panelBottom) - Number(visibleBottom)
    return Number.isFinite(covered) ? Math.max(0, Math.round(covered)) : 0
}

// An older iOS shell overlays the keyboard on its WebView and reports the
// keyboard's height itself. That height is measured from the screen's bottom,
// which is the panel's; WebKit's pan of the page (visualViewport.offsetTop)
// already lifted the page by part of it.
export function resolveNativeOverlayObstruction({
    nativeVisible,
    nativeHeight,
    offsetTop,
}: {
    nativeVisible: boolean
    nativeHeight: number
    offsetTop: number
}): number {
    if (!nativeVisible) return 0
    const covered = (Number(nativeHeight) || 0) - Math.max(0, Number(offsetTop) || 0)
    return Math.max(0, Math.round(covered))
}

// A native shell's measured keyboard edge (lib/keyboardEdgeAdapter) is a
// screen distance: how far the keyboard covers the WebView from its bottom,
// with the WebView's height, in CSS pixels at page scale 1. Mapped into the
// page's layout through the visual viewport (its offset where WebKit or Blink
// panned the page, its scale where it is zoomed), it is the keyboard's top in
// the coordinates the panel is measured in. Without the WebView's height the
// layout viewport's stands in: the WebView is not resized under a measured
// edge.
export function resolveEdgeKeyboardTop({
    edge,
    viewportHeight,
    layoutHeight,
    offsetTop,
    scale,
}: {
    edge: number
    viewportHeight: number | null
    layoutHeight: number
    offsetTop: number
    scale: number
}): number {
    const height = Number(viewportHeight) > 0 ? Number(viewportHeight) : Number(layoutHeight) || 0
    const pageScale = Number(scale) > 0 ? Number(scale) : 1
    const screenTop = height - Math.max(0, Number(edge) || 0)
    return (Number(offsetTop) || 0) + screenTop / pageScale
}

// The panel's cover: what of the keyboard reaches above the panel's bottom.
export function resolveEdgeObstruction({
    panelBottom,
    keyboardTop,
}: {
    panelBottom: number
    keyboardTop: number
}): number {
    const covered = Number(panelBottom) - Number(keyboardTop)
    return Number.isFinite(covered) ? Math.max(0, Math.round(covered)) : 0
}

export type RestViewport = { width: number; height: number } | null

// The viewport's height with no keyboard, for the current width: the tallest
// seen at that width. A rotation starts over.
export function trackRestViewport(
    previous: RestViewport,
    { width, height }: { width: number; height: number },
): RestViewport {
    if (!(width > 0) || !(height > 0)) return previous
    if (!previous || previous.width !== width) return { width, height }
    return height > previous.height ? { width, height } : previous
}

export function resolveViewportShrink(rest: RestViewport, height: number): number {
    if (!rest || !(height > 0)) return 0
    return Math.max(0, Math.round(rest.height - height))
}

export function isKeyboardOccupying(shrink: number): boolean {
    return shrink >= KEYBOARD_PRESENCE_THRESHOLD_PX
}

// Whether a software keyboard is up, for folding and for the row's stale-focus
// blur. The shell's report wins when it says so; otherwise the geometry — a
// replayed resize is a keyboard on its way even before the shell reports it.
export function resolveKeyboardPresent({
    mode,
    nativeVisible,
    obstruction,
    occupied,
}: {
    mode: DockMode
    nativeVisible: boolean | null
    obstruction: number
    occupied: boolean
}): boolean {
    if (nativeVisible === true) return true
    if (mode === 'resize') return occupied
    return obstruction >= KEYBOARD_PRESENCE_THRESHOLD_PX
}

// How far the host is lifted from its resting place so the pill's bottom
// border lands where it should. `restPillBottom` is the pill's bottom above the
// panel's bottom at rest: the host's resting clearance plus the strip's skirt
// under the pill. `margin` is that skirt: the pill keeps it above the keyboard
// as it keeps it above the navbar.
//
// - resize: the resized viewport already puts the panel's foot on the
//   keyboard's edge; while the keyboard occupies it, the resting clearance is
//   dropped by CSS (data-bottom-editor-keyboard="occupied"), a layout change
//   made once per transition, and the skirt stays. Lifted only by a
//   `shortfall`: how much of the keyboard the resize left over the page.
//   Moving the host down instead would overflow the page's bottom. Before the
//   resize lands, a keyboard `expected` at a focus places the pill where that
//   keyboard will: a resizing iOS shell resizes a few frames after WebKit has
//   decided whether the focused field is covered, and WebKit scrolls the whole
//   page to reveal a covered one.
// - overlay: the pill stays on its rail until the keyboard's edge reaches its
//   margin, then rides the edge that margin above it. A dismissal returns it to
//   the rail at once: the rail is a known destination. With the edge measured
//   every frame (`following`, a native shell's keyboard tracker) it rides the
//   edge down instead.
export function resolveDockShift({
    mode,
    obstruction,
    keyboardPresent,
    restPillBottom,
    dismissing,
    margin = 0,
    shortfall = 0,
    expected = 0,
    following = false,
}: {
    mode: DockMode
    obstruction: number
    keyboardPresent: boolean
    restPillBottom: number
    dismissing: boolean
    margin?: number
    shortfall?: number
    expected?: number
    following?: boolean
}): number {
    if (dismissing && !following) return 0
    // The pill's rest above the keyboard's edge once it keeps its margin.
    const rest = Math.max(0, (Number(restPillBottom) || 0) - Math.max(0, Number(margin) || 0))
    if (mode === 'resize') {
        if (Number(expected) > 0) return Math.max(0, Math.round(Number(expected) - rest))
        if (!keyboardPresent) return 0
        return Math.max(0, Math.round(Number(shortfall) || 0))
    }
    if (!keyboardPresent) return 0
    return Math.max(0, Math.round((Number(obstruction) || 0) - rest))
}

// A resized viewport's keyboard can still reach over the page: Android's
// KeyboardAvoidingView measures a window that does not reach the status bar
// against a keyboard placed on the screen, and shrinks the WebView by the
// status bar's height too little (a Samsung on Android 14). The shell's
// report is the keyboard's height above the system controls (React Native's
// Android height), so the page should have shrunk by that plus the system
// controls it spans; iOS reports the height from the screen's bottom, which
// the WebView reaches. Only what is left over lifts the pill, and only for a
// keyboard the shell has reported and the viewport holds.
export function resolveResizeShortfall({
    isAndroid,
    occupied,
    nativeVisible,
    nativeHeight,
    systemControlsBottom,
    shrink,
}: {
    isAndroid: boolean
    occupied: boolean
    nativeVisible: boolean | null
    nativeHeight: number
    systemControlsBottom: number
    shrink: number
}): number {
    if (!occupied || nativeVisible !== true) return 0
    const expected =
        (Number(nativeHeight) || 0) +
        (isAndroid ? Math.max(0, Number(systemControlsBottom) || 0) : 0)
    return Math.max(0, Math.round(expected - (Number(shrink) || 0)))
}

export const DOCK_KEYBOARD_ATTR = 'data-bottom-editor-keyboard'

export type DockKeyboardState = 'occupied' | 'covering' | 'uncovering' | 'returning' | null

// The document-level state CSS keys off: 'occupied' while a resized
// viewport's keyboard holds the bottom edge; 'covering' while an overlay
// keyboard is over the foot (and not being dismissed); 'returning' for the
// moment a resized viewport's keyboard has gone, which runs the navbar's rise.
// 'uncovering' while a measured edge's dismissed keyboard slides off the foot:
// only the navbar's fade reads it; for everything else the foot is free.
export function resolveDockKeyboardState({
    mode,
    obstruction,
    keyboardPresent,
    occupied,
    wasOccupied,
    dismissing,
    following = false,
}: {
    mode: DockMode
    obstruction: number
    keyboardPresent: boolean
    occupied: boolean
    wasOccupied: boolean
    dismissing: boolean
    following?: boolean
}): DockKeyboardState {
    if (mode === 'resize') {
        if (occupied) return 'occupied'
        return wasOccupied ? 'returning' : null
    }
    if (!keyboardPresent || !(obstruction > 0)) return null
    if (!dismissing) return 'covering'
    return following ? 'uncovering' : null
}

// The navbar under a keyboard: hidden while a resized viewport's keyboard
// occupies the edge (it would stand on the keyboard otherwise); faded as an
// overlay keyboard slides over it, and not interactive once covered at all.
// A dismissal gives it back at once, as it does the pill's rail; following a
// measured edge, it comes back with the keyboard's slide, as it went.
export function resolveNavigationCover({
    mode,
    obstruction,
    keyboardPresent,
    occupied,
    navigationHeight,
    dismissing,
    following = false,
}: {
    mode: DockMode
    obstruction: number
    keyboardPresent: boolean
    occupied: boolean
    navigationHeight: number
    dismissing: boolean
    following?: boolean
}): { hidden: boolean; opacity: number } {
    if (mode === 'resize') return { hidden: occupied, opacity: occupied ? 0 : 1 }
    if (!keyboardPresent || (dismissing && !following) || !(obstruction > 0)) {
        return { hidden: false, opacity: 1 }
    }
    const height = Math.max(1, Number(navigationHeight) || 0)
    const covered = Math.min(1, Math.max(0, obstruction / height))
    return { hidden: true, opacity: Math.round((1 - covered) * 1000) / 1000 }
}

// The clearance the thread's end keeps below its last post. Growing is always
// safe; shrinking removes scroll range, and removing range the reader is
// standing in clamps the scroll position — the thread jumps. So a shrink only
// releases as much as lies below the reader's current view.
export function resolveRetainedClearance({
    required,
    current,
    distanceFromBottom,
}: {
    required: number
    current: number | null
    distanceFromBottom: number
}): number {
    const target = Math.max(0, Number(required) || 0)
    if (current == null || !(current > target)) return target
    const releasable = Math.max(0, Number(distanceFromBottom) || 0)
    return Math.max(target, current - releasable)
}

// A touch on the thread while the composer holds focus with no keyboard up —
// Android's Back hides the keyboard without moving focus — releases the focus
// at once, or the tap can bring the keyboard back instead of reaching the
// action. With the keyboard up, the touch may be a scroll: dismissal waits for
// a completed tap.
export function shouldReleaseStaleComposerFocus({
    composerFocused,
    keyboardPresent,
}: {
    composerFocused: boolean
    keyboardPresent: boolean
}): boolean {
    return Boolean(composerFocused && !keyboardPresent)
}

// Whether a dismissal's start already moves the dock: always in overlay (the
// rail is reached by the dock itself), and in a native shell, whose resize
// regrows at the start of the slide. A browser that resizes regrows only after
// the keyboard has gone, so its fold waits for that too — fold and move stay
// one event. Following a measured edge is overlay: the pill folds as Done is
// pressed and rides the keyboard down folded.
export function dockMovesAtDismissalStart({
    mode,
    isNativeShell,
}: {
    mode: DockMode
    isNativeShell: boolean
}): boolean {
    return mode === 'overlay' || isNativeShell
}

// The keyboard as the composer's fold sees it: present, and not being
// dismissed where the dismissal's start moves the dock.
export function resolveFoldKeyboardOpen({
    keyboardPresent,
    dismissing,
    movesAtDismissalStart,
}: {
    keyboardPresent: boolean
    dismissing: boolean
    movesAtDismissalStart: boolean
}): boolean {
    return Boolean(keyboardPresent && !(dismissing && movesAtDismissalStart))
}

// Whether a change of the pill's place is animated from where it was: only a
// return to the rail that starts with the keyboard's slide — a native shell's
// regrow, or an overlay dismissal. An opening in a resized viewport starts
// from below the new viewport, where nothing can be drawn, and an end-time
// browser regrow arrives after the keyboard has gone.
export function shouldAnimateDockReturn({
    mode,
    isNativeShell,
    wasOccupied,
    occupied,
    wasDismissing,
    dismissing,
    delta,
    following = false,
}: {
    mode: DockMode
    isNativeShell: boolean
    wasOccupied: boolean
    occupied: boolean
    wasDismissing: boolean
    dismissing: boolean
    delta: number
    following?: boolean
}): boolean {
    // A measured edge is the motion itself: nothing is animated over it.
    if (following) return false
    if (!(Math.abs(delta) >= DOCK_RETURN_MIN_PX)) return false
    // The pill only ever animates downward, back to its rail.
    if (!(delta < 0)) return false
    if (mode === 'resize') return isNativeShell && wasOccupied && !occupied
    return !wasDismissing && dismissing
}

export type DockSnapshot = {
    // The dock is running (bottom_editor_dock on a phone).
    active: boolean
    // A thread whose pill the dock places is the centred panel: the rows on
    // screen are that thread's, and the keyboard choreography is the dock's.
    docked: boolean
    mode: DockMode
    keyboardPresent: boolean
    occupied: boolean
    obstructing: boolean
    dismissing: boolean
    foldKeyboardOpen: boolean
}

const INACTIVE_SNAPSHOT: DockSnapshot = Object.freeze({
    active: false,
    docked: false,
    mode: 'overlay' as DockMode,
    keyboardPresent: false,
    occupied: false,
    obstructing: false,
    dismissing: false,
    foldKeyboardOpen: false,
})

type Rect = { top: number; bottom: number; height: number }

// The part of a Web Animation the dock uses.
type DockAnimation = {
    cancel(): void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onfinish: ((this: any, event: any) => unknown) | null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    oncancel: ((this: any, event: any) => unknown) | null
}

export type DockElement = {
    getBoundingClientRect(): Rect
    querySelector?(selector: string): DockElement | null
    style: { setProperty(name: string, value: string): void; removeProperty(name: string): void }
    animate?(keyframes: Keyframe[], options: KeyframeAnimationOptions): DockAnimation
    setAttribute?(name: string, value: string): void
    getAttribute?(name: string): string | null
    removeAttribute?(name: string): void
}

export type DockRegistration = {
    panelElement: DockElement
    // The positioning layer: bottom-anchored, moved by the dock shift variable.
    host: DockElement
    // The layer inside it that carries a return animation.
    glide: DockElement
}

export type DockEnvironment = {
    isAndroid: boolean
    isNativeShell: boolean
    nativeCaps(): NativeCaps
    readNativeKeyboard(): { visible: boolean; height: number } | null
    // The height of the system controls the page spans at its bottom (an
    // edge-to-edge Android's navigation bar), in CSS pixels.
    systemControlsBottom(): number
    // An element's computed bottom padding: the host's resting clearance.
    paddingBottom(element: DockElement): number
    // How far the page itself is scrolled, and scrolling it back to its top.
    // The app is one screen that never scrolls as a document; only WebKit's
    // reveal of a focused field ever scrolls it.
    documentScrollTop(): number
    resetDocumentScroll(): void
    // The shell's measured keyboard edge while it streams one to this page
    // (lib/keyboardEdgeAdapter). While there is one it is the only source:
    // the shell has stopped resizing the WebView, and neither the visual
    // viewport nor the shell's keyboard report is read.
    nativeEdge(): {
        visible: boolean
        obstruction: number
        viewportHeight: number | null
        phase?: string
    } | null
    visualViewport(): { offsetTop: number; height: number; scale?: number } | null
    layoutViewport(): { width: number; height: number }
    navigation(): DockElement | null
    root(): DockElement | null
    reduceMotion(): boolean
    requestFrame(callback: () => void): number
    cancelFrame(id: number): void
    setTimer(callback: () => void, ms: number): number
    clearTimer(id: number): void
    listen(onChange: () => void): () => void
}

type PanelId = string | number

type PanelDockState = {
    registration: DockRegistration
    // The pill's bottom above the host's bottom, measured while no return
    // animation moves the glide layer.
    restPillBottom: number | null
    // The lift last written to the host, so a sample that changes nothing
    // writes nothing (the panel re-measures its reserve on each write).
    shift: number | null
    lastPillBottomY: number | null
    returnAnimation: { cancel(): void } | null
}

export function createBottomEditorDock(env: DockEnvironment) {
    const panels = new Map<PanelId, PanelDockState>()
    const listeners = new Set<() => void>()
    let snapshot: DockSnapshot = INACTIVE_SNAPSHOT
    let running = false
    let unlisten: (() => void) | null = null
    let frame = 0
    let activePanelId: PanelId | null = null
    let dismissing = false
    let rest: RestViewport = null
    // A resting viewport seeded from a keyboard already up (start) rather
    // than measured: until the viewport reaches it, its shrink is the
    // keyboard's report, not a resize the page has seen.
    let restSeeded = false
    let wasOccupied = false
    let wasDismissing = false
    let navigationReturnTimer = 0
    // A keyboard expected at a focus, by its height, until the resize takes it
    // (or, under a measured edge, by that edge's cover until the edge reaches
    // it or lands).
    let expected: number | null = null
    let expectTimer = 0

    const emit = () => listeners.forEach(listener => listener())

    const setSnapshot = (next: DockSnapshot) => {
        if (
            snapshot.active === next.active &&
            snapshot.docked === next.docked &&
            snapshot.mode === next.mode &&
            snapshot.keyboardPresent === next.keyboardPresent &&
            snapshot.occupied === next.occupied &&
            snapshot.obstructing === next.obstructing &&
            snapshot.dismissing === next.dismissing &&
            snapshot.foldKeyboardOpen === next.foldKeyboardOpen
        ) {
            return
        }
        snapshot = next
        emit()
    }

    const mode = (): DockMode =>
        resolveDockMode({ isAndroid: env.isAndroid, nativeCaps: env.nativeCaps() })

    const measureShrink = () => {
        const viewport = env.layoutViewport()
        rest = trackRestViewport(rest, viewport)
        if (rest && viewport.height >= rest.height) restSeeded = false
        return resolveViewportShrink(rest, viewport.height)
    }

    const measureObstruction = (state: PanelDockState | undefined) => {
        if (!state) return 0
        const viewport = env.visualViewport()
        if (!viewport) return 0
        const panelRect = state.registration.panelElement.getBoundingClientRect()
        return resolveOverlayObstruction({
            panelBottom: panelRect.bottom,
            visibleBottom: viewport.offsetTop + viewport.height,
        })
    }

    const clearKeyboardState = () => {
        const navigation = env.navigation()
        navigation?.style.removeProperty(BOTTOM_EDITOR_NAVIGATION_OPACITY_VAR)
        navigation?.removeAttribute?.('inert')
        navigation?.removeAttribute?.('aria-hidden')
        env.clearTimer(navigationReturnTimer)
        navigationReturnTimer = 0
        env.root()?.removeAttribute?.(DOCK_KEYBOARD_ATTR)
    }

    // The keyboard's state for the docked thread, on the document: the CSS
    // that drops the resting clearance and skirt in resize mode, and covers or
    // returns the navbar, reads it. Then the navbar itself, if one is shown.
    const applyKeyboardState = ({
        currentMode,
        obstruction,
        keyboardPresent,
        occupied,
        following,
    }: {
        currentMode: DockMode
        obstruction: number
        keyboardPresent: boolean
        occupied: boolean
        following: boolean
    }) => {
        const root = env.root()
        const state = resolveDockKeyboardState({
            mode: currentMode,
            obstruction,
            keyboardPresent,
            occupied,
            wasOccupied,
            dismissing,
            following,
        })
        if (state === 'returning') {
            root?.setAttribute?.(DOCK_KEYBOARD_ATTR, state)
            env.clearTimer(navigationReturnTimer)
            navigationReturnTimer = env.setTimer(() => {
                navigationReturnTimer = 0
                if (env.root()?.getAttribute?.(DOCK_KEYBOARD_ATTR) === 'returning') {
                    env.root()?.removeAttribute?.(DOCK_KEYBOARD_ATTR)
                }
            }, DOCK_NAVIGATION_RETURN_MS)
        } else if (state) {
            env.clearTimer(navigationReturnTimer)
            navigationReturnTimer = 0
            root?.setAttribute?.(DOCK_KEYBOARD_ATTR, state)
        } else if (!navigationReturnTimer) {
            root?.removeAttribute?.(DOCK_KEYBOARD_ATTR)
        }

        const navigation = env.navigation()
        if (!navigation) return
        const cover = resolveNavigationCover({
            mode: currentMode,
            obstruction,
            keyboardPresent,
            occupied,
            navigationHeight: navigation.getBoundingClientRect().height,
            dismissing,
            following,
        })
        navigation.style.setProperty(BOTTOM_EDITOR_NAVIGATION_OPACITY_VAR, String(cover.opacity))
        if (cover.hidden) {
            navigation.setAttribute?.('inert', '')
            navigation.setAttribute?.('aria-hidden', 'true')
        } else {
            navigation.removeAttribute?.('inert')
            navigation.removeAttribute?.('aria-hidden')
        }
    }

    const applyPanel = (
        state: PanelDockState,
        {
            currentMode,
            obstruction,
            keyboardPresent,
            occupied,
            shortfall,
            expectedHeight,
            following,
        }: {
            currentMode: DockMode
            obstruction: number
            keyboardPresent: boolean
            occupied: boolean
            shortfall: number
            expectedHeight: number
            following: boolean
        },
    ) => {
        const { host, glide, panelElement } = state.registration
        const pill = host.querySelector?.('[data-bottom-editor-pill="true"]') || null
        if (!pill) return
        const panelRect = panelElement.getBoundingClientRect()
        // The pill's bottom above the host's bottom, whatever the shift: the
        // host's resting clearance and the strip's skirt. A return animation
        // in flight moves the pill inside the host; keep the last measurement.
        if (!state.returnAnimation || state.restPillBottom == null) {
            const hostRect = host.getBoundingClientRect()
            const pillRect = pill.getBoundingClientRect()
            state.restPillBottom = Math.max(0, Math.round(hostRect.bottom - pillRect.bottom))
        }
        const restPillBottom = state.restPillBottom
        const shift = resolveDockShift({
            mode: currentMode,
            obstruction,
            keyboardPresent,
            restPillBottom,
            dismissing,
            // The skirt: what of the pill's rest lies above the host's
            // clearance.
            margin: restPillBottom - env.paddingBottom(host),
            shortfall,
            expected: expectedHeight,
            following,
        })
        if (state.shift !== shift) {
            state.shift = shift
            host.style.setProperty(BOTTOM_EDITOR_DOCK_SHIFT_VAR, `${shift}px`)
        }

        const pillBottomY = panelRect.bottom - shift - restPillBottom
        const previous = state.lastPillBottomY
        state.lastPillBottomY = pillBottomY
        if (previous == null || typeof glide.animate !== 'function' || env.reduceMotion()) return
        // Screen coordinates grow downward: a pill that was higher than it
        // now stands has a negative delta, and returns down to it.
        const delta = Math.round(previous - pillBottomY)
        // The pill going up for an expected keyboard is not animated: WebKit
        // measures the focused field as the keyboard starts, and an animation
        // from the rail still stands the pill there in that measure.
        if (
            !shouldAnimateDockReturn({
                mode: currentMode,
                isNativeShell: env.isNativeShell,
                wasOccupied,
                occupied,
                wasDismissing,
                dismissing,
                delta,
                following,
            })
        ) {
            return
        }
        state.returnAnimation?.cancel()
        const animation = glide.animate(
            [{ transform: `translateY(${delta}px)` }, { transform: 'translateY(0px)' }],
            { duration: DOCK_RETURN_MS, easing: DOCK_RETURN_EASING },
        )
        state.returnAnimation = animation
        const settle = () => {
            if (state.returnAnimation === animation) state.returnAnimation = null
        }
        animation.onfinish = settle
        animation.oncancel = settle
    }

    const clearExpected = () => {
        expected = null
        if (expectTimer) env.clearTimer(expectTimer)
        expectTimer = 0
    }

    const measureAndApply = () => {
        frame = 0
        if (!running) return
        const shrink = measureShrink()
        // A measured edge means the shell no longer resizes the WebView —
        // unless the window resizes anyway: an Android 11 shell (Galaxy A13,
        // build 21) activates the edge, sends no samples, and the system still
        // shrinks the window for the keyboard. Read as an overlay, the lift
        // stacked on the resize and the navbar stood on the keyboard; a
        // viewport holding a keyboard's shrink is read as the resize it is.
        const activeEdge = env.nativeEdge()
        const edge =
            activeEdge && !restSeeded && mode() === 'resize' && isKeyboardOccupying(shrink)
                ? null
                : activeEdge
        const following = edge != null
        const currentMode: DockMode = following ? 'overlay' : mode()
        const occupied = currentMode === 'resize' && isKeyboardOccupying(shrink)
        const active = activePanelId != null ? panels.get(activePanelId) : undefined
        const native = following ? null : env.readNativeKeyboard()
        // Under a measured edge an expected keyboard stands until the edge
        // reaches its cover or lands short of it.
        if (
            edge &&
            expected != null &&
            (edge.obstruction >= expected || (edge.visible && edge.phase === 'open'))
        ) {
            clearExpected()
        }
        const edgeCover = edge ? Math.max(edge.obstruction, expected ?? 0) : 0
        // Overlay: the shell's measured edge where it streams one; otherwise
        // the visual viewport's account of the keyboard, or an older shell's
        // report of it where the visual viewport says less.
        const visual = edge ? env.visualViewport() : null
        const obstruction = edge
            ? resolveEdgeObstruction({
                  panelBottom: active
                      ? active.registration.panelElement.getBoundingClientRect().bottom
                      : env.layoutViewport().height,
                  keyboardTop: resolveEdgeKeyboardTop({
                      edge: edgeCover,
                      viewportHeight: edge.viewportHeight,
                      layoutHeight: env.layoutViewport().height,
                      offsetTop: visual?.offsetTop || 0,
                      scale: visual?.scale || 1,
                  }),
              })
            : currentMode === 'overlay'
            ? Math.max(
                  measureObstruction(active),
                  native
                      ? resolveNativeOverlayObstruction({
                            nativeVisible: native.visible,
                            nativeHeight: native.height,
                            offsetTop: env.visualViewport()?.offsetTop || 0,
                        })
                      : 0,
              )
            : 0
        const keyboardPresent = edge
            ? edge.visible
            : resolveKeyboardPresent({
                  mode: currentMode,
                  nativeVisible: native ? native.visible : null,
                  obstruction,
                  occupied,
              })
        // A dismissal ends when the keyboard has gone.
        if (dismissing && !keyboardPresent) dismissing = false
        // An expected keyboard stands until the resize takes the viewport; the
        // shell's report of it, once in, is its height.
        if (!edge && expected != null && (currentMode !== 'resize' || occupied)) clearExpected()
        if (expected != null && native?.visible && native.height > 0) expected = native.height
        const shortfall =
            currentMode === 'resize'
                ? resolveResizeShortfall({
                      isAndroid: env.isAndroid,
                      occupied,
                      nativeVisible: native ? native.visible : null,
                      nativeHeight: native ? native.height : 0,
                      systemControlsBottom: env.systemControlsBottom(),
                      shrink,
                  })
                : 0

        // A resized keyboard holds the docked thread's foot: nothing is left
        // for WebKit to reveal, so a page it scrolled while the keyboard was on
        // its way goes back to its top with the resize, not with the close.
        if (currentMode === 'resize' && active && occupied && env.documentScrollTop() > 0) {
            env.resetDocumentScroll()
        }

        // A keyboard expected under a measured edge places the pill and covers
        // the navbar as one that is up; it is not one up, so nothing folds or
        // blurs if it never comes (a hardware keyboard).
        const keyboardPlaced = keyboardPresent || (edge != null && expected != null)

        // The keyboard state goes on the document first: in resize mode it is
        // what drops the resting clearance, and the panels are measured with
        // it applied.
        if (active) {
            applyKeyboardState({
                currentMode,
                obstruction,
                keyboardPresent: keyboardPlaced,
                occupied,
                following,
            })
        } else clearKeyboardState()
        panels.forEach(state =>
            applyPanel(state, {
                currentMode,
                obstruction,
                keyboardPresent: keyboardPlaced,
                occupied,
                shortfall,
                expectedHeight: expected ?? 0,
                following,
            }),
        )

        wasOccupied = occupied
        wasDismissing = dismissing
        setSnapshot({
            active: true,
            docked: active != null,
            mode: currentMode,
            keyboardPresent,
            occupied,
            obstructing: currentMode === 'overlay' ? obstruction > 0 : occupied,
            dismissing,
            foldKeyboardOpen: resolveFoldKeyboardOpen({
                keyboardPresent,
                // A keyboard the measured edge says is closing is a dismissal
                // from its start, focus kept or not: Android's back gesture
                // and hide key close it without a blur, and the pill then only
                // folded once the keyboard had landed.
                dismissing: dismissing || edge?.phase === 'closing',
                movesAtDismissalStart: dockMovesAtDismissalStart({
                    mode: currentMode,
                    isNativeShell: env.isNativeShell,
                }),
            }),
        })
    }

    const schedule = () => {
        if (!running || frame) return
        frame = env.requestFrame(measureAndApply)
    }

    return {
        start() {
            if (running) return
            running = true
            const viewport = env.layoutViewport()
            const native = env.readNativeKeyboard()
            // A remount can happen while the keyboard is still up. Keep a
            // previously measured full-height viewport; on a fresh mount in a
            // resize shell, use its keyboard height to seed that baseline.
            if (
                (!rest || rest.width !== viewport.width) &&
                mode() === 'resize' &&
                native?.visible &&
                native.height >= KEYBOARD_PRESENCE_THRESHOLD_PX
            ) {
                rest = { width: viewport.width, height: viewport.height + native.height }
                restSeeded = true
            } else {
                rest = trackRestViewport(rest, viewport)
            }
            unlisten = env.listen(schedule)
            measureAndApply()
        },
        stop() {
            if (!running) return
            running = false
            unlisten?.()
            unlisten = null
            if (frame) env.cancelFrame(frame)
            frame = 0
            env.clearTimer(navigationReturnTimer)
            navigationReturnTimer = 0
            panels.forEach(state => {
                state.returnAnimation?.cancel()
                state.registration.host.style.removeProperty(BOTTOM_EDITOR_DOCK_SHIFT_VAR)
                state.shift = null
                state.lastPillBottomY = null
            })
            clearKeyboardState()
            clearExpected()
            dismissing = false
            wasOccupied = false
            wasDismissing = false
            setSnapshot(INACTIVE_SNAPSHOT)
        },
        register(panelId: PanelId, registration: DockRegistration) {
            panels.set(panelId, {
                registration,
                restPillBottom: null,
                shift: null,
                lastPillBottomY: null,
                returnAnimation: null,
            })
            schedule()
            return () => {
                const state = panels.get(panelId)
                if (!state || state.registration !== registration) return
                state.returnAnimation?.cancel()
                registration.host.style.removeProperty(BOTTOM_EDITOR_DOCK_SHIFT_VAR)
                panels.delete(panelId)
                schedule()
            }
        },
        setActivePanel(panelId: PanelId | null) {
            if (activePanelId === panelId) return
            activePanelId = panelId
            schedule()
        },
        // Focus left every keyboard input with a keyboard up (true), or came
        // back to one (false).
        setDismissing(next: boolean) {
            // Focus gone before the keyboard it expected came: the pill goes
            // back to its rail.
            if (next && expected != null) {
                clearExpected()
                schedule()
            }
            if (dismissing === next) return
            if (next && !snapshot.keyboardPresent) return
            dismissing = next
            if (!running) return
            // Apply now: the return starts with the keyboard's slide.
            if (frame) env.cancelFrame(frame)
            measureAndApply()
        },
        // Focus landed in a keyboard input with no keyboard up. Where the
        // viewport resizes for the keyboard (a resizing iOS shell), the pill goes
        // where a keyboard of this height will put it, ahead of the resize:
        // WebKit decides at the keyboard's start whether the focused field is
        // covered, and scrolls the whole page to reveal a covered one. Under a
        // shell's measured edge the height is the last landed edge's cover:
        // the pill goes there at the focus, as the resize puts it in legacy,
        // since a page busy opening the editor paints none of the edge's
        // samples while the keyboard rises over it.
        expectKeyboard(height: number | null | undefined) {
            if (!(Number(height) > 0) || snapshot.keyboardPresent) return
            expected = Number(height)
            if (expectTimer) env.clearTimer(expectTimer)
            expectTimer = env.setTimer(() => {
                expectTimer = 0
                expected = null
                schedule()
            }, DOCK_EXPECT_KEYBOARD_MS)
            if (!running) return
            if (frame) env.cancelFrame(frame)
            measureAndApply()
        },
        // A keyboard expected at a focus is still on its way.
        expectingKeyboard(): boolean {
            return expected != null
        },
        update: schedule,
        // How far the layout viewport is shrunk below its resting height right
        // now (resize mode's keyboard), for sizing things by the resting
        // scrollport rather than the shrunk one.
        currentShrink(): number {
            if (!running || mode() !== 'resize') return 0
            const viewport = env.layoutViewport()
            const shrink = resolveViewportShrink(rest, viewport.height)
            // Under a measured edge only a window that resized anyway counts.
            return env.nativeEdge() && (restSeeded || !isKeyboardOccupying(shrink)) ? 0 : shrink
        },
        subscribe(listener: () => void) {
            listeners.add(listener)
            return () => {
                listeners.delete(listener)
            }
        },
        getSnapshot(): DockSnapshot {
            return snapshot
        },
    }
}

export type BottomEditorDock = ReturnType<typeof createBottomEditorDock>

// The page's own environment. `nativeKeyboardChange` is the shell's keyboard
// report (treechat-mobile nativeKeyboardBridge.ts); it also leaves the latest
// snapshot on window.KNOVIGATOR_NATIVE_KEYBOARD.
type ShellWindow = Window & {
    KNOVIGATOR_IS_MOBILE?: boolean
    KNOVIGATOR_NATIVE_CAPS?: NativeCaps
    KNOVIGATOR_NATIVE_KEYBOARD?: { visible?: unknown; height?: unknown }
}

const NATIVE_KEYBOARD_CHANGE_EVENT = 'nativeKeyboardChange'

export function createBrowserDockEnvironment({
    isAndroid,
}: {
    isAndroid: boolean
}): DockEnvironment {
    const shell = window as ShellWindow
    return {
        isAndroid,
        isNativeShell: Boolean(shell.KNOVIGATOR_IS_MOBILE),
        nativeCaps: () => shell.KNOVIGATOR_NATIVE_CAPS,
        readNativeKeyboard: () => {
            if (shell.KNOVIGATOR_NATIVE_CAPS?.keyboardState !== true) return null
            const state = shell.KNOVIGATOR_NATIVE_KEYBOARD
            if (!state || typeof state.visible !== 'boolean') return null
            const height = Number(state.height)
            return { visible: state.visible, height: Number.isFinite(height) ? height : 0 }
        },
        // Written on the root by the shell (treechat-mobile App.tsx).
        systemControlsBottom: () => {
            const value = parseFloat(
                document.documentElement.style.getPropertyValue('--native-system-controls-bottom'),
            )
            return Number.isFinite(value) ? value : 0
        },
        paddingBottom: element => {
            const value = parseFloat(getComputedStyle(element as unknown as Element).paddingBottom)
            return Number.isFinite(value) ? value : 0
        },
        documentScrollTop: () => document.scrollingElement?.scrollTop || 0,
        resetDocumentScroll: () => window.scrollTo(0, 0),
        nativeEdge: () => peekKeyboardEdge(),
        visualViewport: () => {
            const viewport = window.visualViewport
            return viewport
                ? { offsetTop: viewport.offsetTop, height: viewport.height, scale: viewport.scale }
                : null
        },
        // The layout viewport, not innerHeight: WebKit and Blink disagree
        // about whether innerHeight follows the keyboard.
        layoutViewport: () => ({
            width: document.documentElement.clientWidth,
            height: document.documentElement.clientHeight,
        }),
        navigation: () => document.querySelector<HTMLElement>('[data-bottom-navigation]'),
        root: () => document.documentElement,
        reduceMotion: () =>
            Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches),
        requestFrame: callback => window.requestAnimationFrame(callback),
        cancelFrame: id => window.cancelAnimationFrame(id),
        setTimer: (callback, ms) => window.setTimeout(callback, ms),
        clearTimer: id => window.clearTimeout(id),
        listen: onChange => {
            const viewport = window.visualViewport
            const unsubscribeEdge = getKeyboardEdgeAdapter().subscribe(onChange)
            window.addEventListener('resize', onChange)
            window.addEventListener('orientationchange', onChange)
            window.addEventListener(NATIVE_KEYBOARD_CHANGE_EVENT, onChange)
            viewport?.addEventListener('resize', onChange)
            viewport?.addEventListener('scroll', onChange)
            return () => {
                unsubscribeEdge()
                window.removeEventListener('resize', onChange)
                window.removeEventListener('orientationchange', onChange)
                window.removeEventListener(NATIVE_KEYBOARD_CHANGE_EVENT, onChange)
                viewport?.removeEventListener('resize', onChange)
                viewport?.removeEventListener('scroll', onChange)
            }
        },
    }
}

let pageDock: BottomEditorDock | null = null

// The page's one dock. PanelController starts and stops it; panels register
// their composer hosts with it; rows read its snapshot.
export function getBottomEditorDock({ isAndroid }: { isAndroid: boolean }): BottomEditorDock {
    if (!pageDock) pageDock = createBottomEditorDock(createBrowserDockEnvironment({ isAndroid }))
    return pageDock
}

export function peekBottomEditorDockSnapshot(): DockSnapshot {
    return pageDock ? pageDock.getSnapshot() : INACTIVE_SNAPSHOT
}

export function inactiveBottomEditorDockSnapshot(): DockSnapshot {
    return INACTIVE_SNAPSHOT
}
